import React from "react";
import { useIntl } from "react-intl";
import { PeekChip, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { UnderlinedTooltip } from "@components/common/underlined-tooltip";
import quadrantMessages from "./messages.i18n";
import { CartesianGrid, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, XAxis, YAxis, } from "recharts";
import { Ellipse } from "@piiqtechnologies/svg-icons";
export const quadrantChartQuadrants = ({ xDomain, yDomain, splitX, splitY, }) => {
    const theme = useTheme();
    const intl = useIntl();
    return [
        {
            id: "monitor",
            title: "Monitor",
            xRange: [xDomain[0], splitX],
            yRange: [splitY, yDomain[1]],
            fill: theme.palette.blue[200],
            titleColor: theme.palette.blue[500],
            tooltip: intl.formatMessage(quadrantMessages.tooltipMonitor),
        },
        {
            id: "maintain",
            title: "Maintain",
            xRange: [splitX, xDomain[1]],
            yRange: [splitY, yDomain[1]],
            fill: theme.palette.success.light,
            titleColor: theme.palette.success.main,
            tooltip: intl.formatMessage(quadrantMessages.tooltipMaintain),
        },
        {
            id: "improve",
            title: "Improve",
            xRange: [xDomain[0], splitX],
            yRange: [yDomain[0], splitY],
            fill: theme.palette.warning.light,
            titleColor: theme.palette.warning.main,
            tooltip: intl.formatMessage(quadrantMessages.tooltipImprove),
        },
        {
            id: "priority-fix",
            title: "Priority Fix",
            xRange: [splitX, xDomain[1]],
            yRange: [yDomain[0], splitY],
            fill: theme.palette.error.light,
            titleColor: theme.palette.error.main,
            tooltip: intl.formatMessage(quadrantMessages.tooltipPriorityFix),
        },
    ];
};
const CustomDot = ({ cx, cy, payload, onPointHover, onPointClick, getPointColor, isActive }) => {
    if (!payload || cx == null || cy == null)
        return null;
    const fill = getPointColor(payload);
    return (React.createElement("foreignObject", { x: cx + 6, y: cy - 14, width: "100%", height: 40, style: {
            overflow: "visible",
            pointerEvents: "none",
        } },
        React.createElement("div", { style: { display: "inline-block", pointerEvents: "auto" } },
            React.createElement(PeekChip, { variant: "outlined", label: React.createElement(PeekStack, { direction: "row", alignItems: "center", gap: 0.5 },
                    React.createElement(Ellipse, { color: fill }),
                    " ",
                    payload.name), size: "small", onMouseEnter: () => onPointHover === null || onPointHover === void 0 ? void 0 : onPointHover(payload), onMouseLeave: () => onPointHover === null || onPointHover === void 0 ? void 0 : onPointHover(undefined), onClick: () => onPointClick === null || onPointClick === void 0 ? void 0 : onPointClick(payload), sx: {
                    border: "1px solid transparent",
                    whiteSpace: "nowrap",
                    backgroundColor: isActive ? "white" : "transparent",
                    color: isActive ? fill : "inherit",
                    borderColor: isActive ? fill : "transparent",
                    transition: "all 10ms ease-in-out",
                    "&.MuiChip-clickable:hover": {
                        backgroundColor: "white",
                        color: fill,
                        borderColor: fill,
                        cursor: "pointer",
                    },
                    ".MuiChip-icon": {
                        width: "12px !important",
                    },
                } }))));
};
export const QuadrantChart = ({ data, quadrants, xDomain, yDomain, splitX, splitY, height = 480, lowLabel = "Low", highLabel = "High", positiveLabel = "+Gap", negativeLabel = "-Gap", activePointName, onPointHover, onPointClick, }) => {
    var _a;
    const theme = useTheme();
    const [hoveredPoint, setHoveredPoint] = React.useState();
    const handlePointHover = (point) => {
        setHoveredPoint(point);
        onPointHover === null || onPointHover === void 0 ? void 0 : onPointHover(point);
    };
    // Determine which point should have elevated z-index
    const activePointNameResolved = (_a = hoveredPoint === null || hoveredPoint === void 0 ? void 0 : hoveredPoint.name) !== null && _a !== void 0 ? _a : activePointName;
    // Reorder data so active point renders last (appears on top in SVG)
    // Add stable keys to prevent x/y swap during animation
    const orderedData = React.useMemo(() => {
        if (!activePointNameResolved)
            return data;
        const activeIndex = data.findIndex((p) => p.name === activePointNameResolved);
        if (activeIndex === -1)
            return data;
        // Create new array with active point at the end
        const reordered = [...data];
        const [activePoint] = reordered.splice(activeIndex, 1);
        reordered.push(activePoint);
        return reordered;
    }, [data, activePointNameResolved]);
    const getPointColor = (point) => {
        var _a, _b;
        // If point has explicit status from server, use it to find the correct quadrant
        if (point.status) {
            const normalizedStatus = point.status
                .trim()
                .toLowerCase()
                .replace(/[\s_-]+/g, "-");
            const quadrant = quadrants.find((area) => area.id.toLowerCase() === normalizedStatus);
            if (quadrant) {
                return (_a = quadrant.titleColor) !== null && _a !== void 0 ? _a : theme.palette.primary.main;
            }
        }
        // Fallback to position-based calculation
        const quadrant = quadrants.find((area) => {
            const [xMin, xMax] = area.xRange;
            const [yMin, yMax] = area.yRange;
            // Include min, exclude max - prevents overlap at boundaries
            // Point exactly at splitX belongs to right quadrant, at splitY belongs to top quadrant
            return point.x >= xMin && point.x < xMax && point.y >= yMin && point.y < yMax;
        });
        return (_b = quadrant === null || quadrant === void 0 ? void 0 : quadrant.titleColor) !== null && _b !== void 0 ? _b : theme.palette.primary.main;
    };
    const CustomXAxisTick = ({ x, y, index }) => {
        var _a;
        const labels = [lowLabel, "", highLabel];
        const label = (_a = labels[index]) !== null && _a !== void 0 ? _a : "";
        return (React.createElement("text", { x: x, y: y, dy: 14, textAnchor: "middle", fill: theme.palette.grey[300], fontSize: 12 }, label));
    };
    const CustomYAxisTick = ({ x, y, payload }) => {
        let label = "";
        if ((payload === null || payload === void 0 ? void 0 : payload.value) === yDomain[1])
            label = positiveLabel;
        else if ((payload === null || payload === void 0 ? void 0 : payload.value) === splitY)
            label = "0";
        else if ((payload === null || payload === void 0 ? void 0 : payload.value) === yDomain[0])
            label = negativeLabel;
        return (React.createElement("text", { x: x - 8, y: y, dy: 4, textAnchor: "end", fill: theme.palette.grey[300], fontSize: 12 }, label));
    };
    return (React.createElement(ResponsiveContainer, { width: "100%", height: height },
        React.createElement(ScatterChart, { margin: { top: 30, right: 30, bottom: 40, left: 40 } },
            React.createElement(CartesianGrid, { strokeDasharray: "3 3", vertical: false }),
            React.createElement(XAxis, { type: "number", dataKey: "x", domain: xDomain, ticks: [xDomain[0], splitX, xDomain[1]], tick: React.createElement(CustomXAxisTick, null) }),
            React.createElement(YAxis, { type: "number", dataKey: "y", domain: yDomain, ticks: [yDomain[1], splitY, yDomain[0]], tick: React.createElement(CustomYAxisTick, null) }),
            quadrants.map((quadrant) => {
                var _a;
                return (React.createElement(ReferenceArea, { key: quadrant.id, x1: quadrant.xRange[0], x2: quadrant.xRange[1], y1: quadrant.yRange[0], y2: quadrant.yRange[1], fill: quadrant.fill, fillOpacity: 1, label: quadrant.tooltip
                        ? ({ viewBox }) => {
                            var _a;
                            const { x, y, width } = viewBox;
                            return (React.createElement("foreignObject", { x: x, y: y + 6, width: width, height: 30 },
                                React.createElement(PeekStack, { direction: "row", justifyContent: "center" },
                                    React.createElement(UnderlinedTooltip, { tooltip: quadrant.tooltip, sx: {
                                            color: (_a = quadrant.titleColor) !== null && _a !== void 0 ? _a : theme.palette.text.secondary,
                                            fontSize: 12,
                                            fontWeight: 500,
                                        } }, quadrant.title))));
                        }
                        : {
                            value: quadrant.title,
                            position: "insideTop",
                            offset: 10,
                            fill: (_a = quadrant.titleColor) !== null && _a !== void 0 ? _a : theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 500,
                        } }));
            }),
            React.createElement(ReferenceLine, { x: splitX, width: 10, stroke: "white" }),
            React.createElement(ReferenceLine, { y: splitY, width: 10, stroke: "white" }),
            React.createElement(Scatter, { data: orderedData, isAnimationActive: false, shape: (props) => {
                    var _a;
                    const isActive = ((_a = props.payload) === null || _a === void 0 ? void 0 : _a.name) === activePointNameResolved;
                    return (React.createElement(CustomDot, Object.assign({}, props, { getPointColor: getPointColor, isActive: isActive, onPointHover: handlePointHover, onPointClick: onPointClick })));
                } }))));
};
export const QuadrantChartTitle = ({ children }) => (React.createElement(PeekTypography, { variant: "subtitle2", color: "text.secondary" }, children));
export default QuadrantChart;
